import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient, useQuery, useIsFetching } from '@tanstack/react-query';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import AddressList from '../../Common/Address/AddressList';
import UnifiedPhoneInput from '../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput';
import { useAuth } from '../../../context/AuthContext';

const CustomerForm = ({ customer, onSuccess }) => {
    const queryClient = useQueryClient();
    const isCreating = !customer;
    const { user: currentUser } = useAuth();

    // Détecter le chargement lors du refetch après invalidation
    const isRefetchingCustomers = useIsFetching({ queryKey: ['customers'] }) > 0;
    const [isInvalidating, setIsInvalidating] = useState(false);
    const [addresses, setAddresses] = useState(customer?.addresses || []);
    const [phoneData, setPhoneData] = useState({
        phone: customer?.phone || '',
        phonePrefix: customer?.phonePrefix || '+33'
    });
    const [therapistSearchQuery, setTherapistSearchQuery] = useState('');
    const [selectedTherapist, setSelectedTherapist] = useState(null);
    const [showTherapistResults, setShowTherapistResults] = useState(false);
    const [filteredTherapists, setFilteredTherapists] = useState([]);

    // Récupérer tous les thérapeutes (comme dans TherapistsManagement)
    const {
        data: therapistsData,
        isLoading: therapistsLoading,
    } = useQuery({
        queryKey: ['therapists'],
        queryFn: async () => {
            const response = await api.get(API.ADMIN_ALL_THERAPISTS);
            return response.data.values;
        }
    });


    // Mettre à jour selectedTherapist lorsque les données du client changent
    useEffect(() => {
        if (customer?.therapist) {
            setSelectedTherapist(customer.therapist);
        }
    }, [customer]);

    // Filtrer les thérapeutes en fonction de la recherche
    useEffect(() => {
        if (!therapistsData) return;

        // Vérifier si la requête est un nombre (ID) ou un texte
        const isNumericQuery = !isNaN(therapistSearchQuery) && therapistSearchQuery.trim() !== '';
        const searchLength = isNumericQuery ? 1 : 2; // Exiger seulement 1 caractère pour les IDs

        if (therapistSearchQuery.length >= searchLength) {
            const lowercaseQuery = therapistSearchQuery.toLowerCase();
            let filtered = therapistsData.filter(therapist => {
                // Convertir explicitement l'ID en chaîne pour la comparaison
                const therapistId = String(therapist.id);

                if (isNumericQuery) {
                    // Si la requête est numérique, chercher uniquement par ID
                    return therapistId.includes(therapistSearchQuery);
                } else {
                    // Sinon, chercher par nom, prénom ou ID
                    return therapist.firstname.toLowerCase().includes(lowercaseQuery) ||
                        therapist.lastname.toLowerCase().includes(lowercaseQuery) ||
                        therapistId.includes(therapistSearchQuery);
                }
            });

            // Tri des résultats (inversé pour les recherches par ID)
            if (isNumericQuery) {
                // Tri par ID décroissant pour les recherches numériques
                filtered.sort((a, b) => Number(a.id) - Number(b.id));
            } else {
                // Tri par nom pour les recherches textuelles
                filtered.sort((a, b) => {
                    const nameA = `${a.firstname} ${a.lastname}`.toLowerCase();
                    const nameB = `${b.firstname} ${b.lastname}`.toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            }

            // Ajouter l'utilisateur connecté si la recherche correspond
            if (currentUser) {
                const currentUserId = String(currentUser.id);

                if (isNumericQuery) {
                    // Pour une recherche numérique, inclure l'utilisateur si son ID correspond
                    if (currentUserId.includes(therapistSearchQuery) &&
                        !filtered.some(t => t.id === currentUser.id)) {
                        const currentUserAsTherapist = {
                            id: currentUser.id,
                            firstname: currentUser.firstname,
                            lastname: currentUser.lastname,
                            displayName: "Moi"
                        };
                        filtered = [currentUserAsTherapist, ...filtered];
                    }
                } else if (
                    lowercaseQuery === "" ||
                    "moi".includes(lowercaseQuery) ||
                    (currentUser.firstname && currentUser.firstname.toLowerCase().includes(lowercaseQuery)) ||
                    (currentUser.lastname && currentUser.lastname.toLowerCase().includes(lowercaseQuery))
                ) {
                    // Pour une recherche textuelle, inclure l'utilisateur si son nom correspond
                    if (!filtered.some(t => t.id === currentUser.id)) {
                        const currentUserAsTherapist = {
                            id: currentUser.id,
                            firstname: currentUser.firstname,
                            lastname: currentUser.lastname,
                            displayName: "Moi"
                        };
                        filtered = [currentUserAsTherapist, ...filtered];
                    }
                }
            }

            setFilteredTherapists(filtered);
            setShowTherapistResults(true);
        } else {
            setShowTherapistResults(false);
        }
    }, [therapistSearchQuery, therapistsData, currentUser]);

    const mutation = useMutation({
        mutationFn: (data) => {
            const requestData = {
                customerData: {
                    firstname: data.firstname,
                    lastname: data.lastname,
                    email: data.email,
                    phone: data.phone,
                    phonePrefix: data.phonePrefix,
                    isVerified: data.isVerified
                },
                therapistId: data.therapistId
            };

            if (isCreating) {
                return api.post(API.ADMIN_CREATE_CUSTOMER, requestData);
            }
            return api.patch(`${API.ADMIN_UPDATE_CUSTOMER}/${customer.id}`, requestData);
        },
        onSuccess: async () => {
            setIsInvalidating(true);
            await queryClient.invalidateQueries({ queryKey: ['customers'] });
            setIsInvalidating(false);
            if (onSuccess) onSuccess();
        }
    });

    useEffect(() => {
        setAddresses(customer?.addresses || []);
    }, [customer]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = {
            firstname: formData.get('firstname'),
            lastname: formData.get('lastname'),
            email: formData.get('email').toLowerCase().trim(),
            phone: phoneData.phone,
            phonePrefix: phoneData.phonePrefix,
            isVerified: formData.get('isVerified') ? true : false,
            therapistId: selectedTherapist?.id || null
        };
        const response = mutation.mutate(data);
        console.log(response);
    };

    const handleTherapistSelect = (therapist) => {
        setSelectedTherapist(therapist);
        setTherapistSearchQuery('');
        setShowTherapistResults(false);
    };

    return (
        <div className="space-y-6">

            <form id="customer-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-6">
                {/* Informations personnelles */}
                <div className="space-y-4">
                    <h4 className="text-lg font-semibold">Informations personnelles</h4>
                    <div className="card bg-base-200 p-4 space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nom</span>
                            </label>
                            <input
                                type="text"
                                name="lastname"
                                className="input input-bordered"
                                defaultValue={customer?.lastname || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Prénom</span>
                            </label>
                            <input
                                type="text"
                                name="firstname"
                                className="input input-bordered"
                                defaultValue={customer?.firstname || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Email</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                className="input input-bordered"
                                defaultValue={customer?.email || ''}
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Téléphone</span>
                            </label>
                            <UnifiedPhoneInput
                                name="phone"
                                value={phoneData.phone}
                                phonePrefix={phoneData.phonePrefix}
                                onChange={(e) => setPhoneData(prev => ({
                                    ...prev,
                                    [e.target.name]: e.target.value
                                }))}
                                placeholder="Entrez le numéro de téléphone"
                            />
                        </div>



                        {/* Sélection du thérapeute */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Thérapeute associé
                                    {customer?.therapist && (
                                        <span className="text-primary font-medium ml-1">
                                            - {customer.therapist.displayName || `${customer.therapist.firstname} ${customer.therapist.lastname}`}
                                        </span>
                                    )}
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Rechercher un thérapeute (nom: min 2 caractères, ID: min 1 chiffre)"
                                    value={therapistSearchQuery}
                                    onChange={(e) => setTherapistSearchQuery(e.target.value)}
                                    onFocus={() => {
                                        // Vérifier si la requête est un ID (numérique) ou un texte
                                        const isNumeric = !isNaN(therapistSearchQuery) && therapistSearchQuery.trim() !== '';
                                        const minLength = isNumeric ? 1 : 2;

                                        if (therapistSearchQuery.length >= minLength) {
                                            setShowTherapistResults(true);
                                        }
                                    }}
                                />

                                {/* Liste des thérapeutes trouvés */}
                                {showTherapistResults && filteredTherapists.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        <ul>
                                            {filteredTherapists.map(therapist => (
                                                <li
                                                    key={therapist.id}
                                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                                                    onClick={() => handleTherapistSelect(therapist)}
                                                >
                                                    <span>{therapist.displayName || `${therapist.firstname} ${therapist.lastname}`}</span>
                                                    <span className="text-gray-500">ID: {therapist.id}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {therapistsLoading && (
                                    <div className="mt-2">Chargement des thérapeutes...</div>
                                )}

                                {/* Affichage du thérapeute sélectionné */}
                                {selectedTherapist && (
                                    <div className="mt-2 p-2 border rounded-md bg-base-100 flex justify-between items-center">
                                        <div>
                                            <p className="font-medium">
                                                {selectedTherapist.displayName ||
                                                    `${selectedTherapist.firstname} ${selectedTherapist.lastname}`}
                                            </p>
                                            <p className="text-sm text-gray-500">ID: {selectedTherapist.id}</p>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-ghost"
                                            onClick={() => setSelectedTherapist(null)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-control flex items-center flex-row gap-2">
                            <label className="label" htmlFor="isVerified">
                                <span className="label-text">L'utilisateur est déjà vérifié ?</span>
                            </label>
                            <input
                                type="checkbox"
                                id="isVerified"
                                name="isVerified"
                                className="checkbox checkbox-primary checkbox-sm"
                                defaultChecked={customer?.isVerified || false}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full mt-4"
                            disabled={mutation.isPending || isInvalidating || isRefetchingCustomers}
                        >
                            {mutation.isPending || isInvalidating || isRefetchingCustomers ? 'Enregistrement...' : (isCreating ? 'Créer' : 'Mettre à jour')}
                        </button>
                    </div>
                </div>

                {/* Adresses */}
                {!isCreating && (
                    <div className="space-y-4">
                        <h4 className="text-lg font-semibold">Adresses</h4>
                        <div className="card bg-base-200 p-4">
                            <AddressList
                                userId={customer.id}
                                userType="customer"
                                addresses={addresses}
                                setAddresses={setAddresses}
                            />
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CustomerForm; 